import { NgxsDataRepository } from '@angular-ru/ngxs/repositories'
import { Computed, DataAction, Payload } from '@angular-ru/ngxs/decorators'
import { distinctUntilChanged, distinctUntilKeyChanged, Observable } from 'rxjs'
import { map } from 'rxjs/operators'

export interface RequestModelBase<T> {
  loading?: boolean
  error?: Error | null
  data?: T
}

export type RequestModel<
  T,
  Extends extends object = object
> = RequestModelBase<T> & Extends

export abstract class RequestRepository<
  T,
  Extends extends object = object
> extends NgxsDataRepository<RequestModel<T, Extends>> {
  /**
   * Returns the loading status as an observable
   */
  @Computed()
  public get loading$(): Observable<boolean | undefined> {
    return this.state$.pipe(
      distinctUntilKeyChanged('loading'),
      map((state) => state?.loading)
    )
  }

  /**
   * Returns the loading status as an observable
   */
  @Computed()
  public get error$(): Observable<Error | null | undefined> {
    return this.state$.pipe(
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous.error) === JSON.stringify(current.error)
      ),
      map((state) => state.error)
    )
  }

  /**
   * Returns the data as an observable
   */
  @Computed()
  public get data$(): Observable<T | undefined> {
    return this.state$.pipe(
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous.data) === JSON.stringify(current.data)
      ),
      map((state) => state.data)
    )
  }

  /**
   * Set the loading of the entity state to true of false
   */
  @DataAction()
  public setLoading(@Payload('loading') loading: boolean): void {
    const state = this.getState()
    this.patchState({ ...state, loading })
  }

  /**
   * Set any Error that could happen with the entity api
   */
  @DataAction()
  public setError(@Payload('error') error: Error | null): void {
    const state = this.getState()
    this.patchState({ ...state, error })
  }

  /**
   * Set the data returned by the request
   */
  @DataAction()
  public setData(@Payload('data') data: T): void {
    const state = this.getState()
    this.patchState({ ...state, data })
  }
}
